import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTours } from '@/hooks/useTours';
import { useFavorites } from '@/hooks/useFavorites';
import { useTourDiscounts, resolveTourDiscount, applyTourDiscount } from '@/hooks/useTourDiscounts';
import SelectChevron from '@/components/base/SelectChevron';

export default function ToursGrid() {
  const { tours, loading, error } = useTours();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { discounts } = useTourDiscounts();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [styleFilter, setStyleFilter] = useState('All');
  const [durationFilter, setDurationFilter] = useState(searchParams.get('duration') || 'All');

  useEffect(() => {
    const duration = searchParams.get('duration');
    if (duration) setDurationFilter(duration);
  }, [searchParams]);
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const itemsPerPage = 12;

  const filteredTours = tours.filter((tour) => {
    const matchesSearch = tour.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStyle = styleFilter === 'All' || tour.style === styleFilter;
    const matchesDuration =
      durationFilter === 'All' ||
      (durationFilter === '1-3 Days' && parseInt(tour.duration) <= 3) ||
      (durationFilter === '4-7 Days' && parseInt(tour.duration) >= 4 && parseInt(tour.duration) <= 7) ||
      (durationFilter === '8+ Days' && parseInt(tour.duration) >= 8);
    return matchesSearch && matchesStyle && matchesDuration;
  });

  const totalPages = Math.ceil(filteredTours.length / itemsPerPage);
  const paginatedTours = filteredTours.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <section id="tour-packages" className="w-full bg-background-50 py-10 md:py-14 lg:py-16 scroll-mt-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header + Filters */}
        <div className="mb-5 lg:mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <h2 className="text-lg font-bold tracking-tight text-foreground-950 lg:text-[30px]">
              {t('tours.gridTitle')}
            </h2>
            {/* Desktop filters */}
            <div className="hidden lg:flex items-center gap-2">
              <div className="flex h-10 w-[260px] items-center gap-2 rounded-xl border border-background-300/60 bg-white px-3 text-sm focus-within:ring-2 focus-within:ring-primary-400/30">
                <i className="ri-search-line text-secondary-400 shrink-0" />
                <input
                  type="search"
                  placeholder={t('tours.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="h-10 min-w-0 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-secondary-400"
                />
              </div>
              <div className="relative">
                <select
                  value={styleFilter}
                  onChange={(e) => { setStyleFilter(e.target.value); setCurrentPage(1); }}
                  className="h-10 w-[199px] appearance-none rounded-xl border border-background-300/60 bg-white px-3 pr-8 text-sm text-secondary-500 outline-none cursor-pointer focus:ring-2 focus:ring-primary-400/30"
                >
                  <option value="All">{t('tours.styleAll')}</option>
                  <option value="multi-day">{t('tours.styleMultiDay')}</option>
                  <option value="day">{t('tours.styleDayTour')}</option>
                  <option value="cruise">{t('tours.styleCruise')}</option>
                </select>
                <SelectChevron className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400" />
              </div>
              <div className="relative">
                <select
                  value={durationFilter}
                  onChange={(e) => { setDurationFilter(e.target.value); setCurrentPage(1); }}
                  className="h-10 w-[199px] appearance-none rounded-xl border border-background-300/60 bg-white px-3 pr-8 text-sm text-secondary-500 outline-none cursor-pointer focus:ring-2 focus:ring-primary-400/30"
                >
                  <option value="All">{t('tours.durationAll')}</option>
                  <option value="1-3 Days">{t('tours.days1to3')}</option>
                  <option value="4-7 Days">{t('tours.days4to7')}</option>
                  <option value="8+ Days">{t('tours.days8plus')}</option>
                </select>
                <SelectChevron className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400" />
              </div>
            </div>
          </div>

          {/* Mobile filters */}
          <div className="flex flex-col gap-2 mt-4 lg:hidden">
            <div className="flex h-10 w-full items-center gap-2 rounded-xl border border-background-300/60 bg-white px-3 text-sm focus-within:ring-2 focus-within:ring-primary-400/30">
              <i className="ri-search-line text-secondary-400 shrink-0" />
              <input
                type="search"
                placeholder={t('tours.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="h-10 min-w-0 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-secondary-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <select
                  value={styleFilter}
                  onChange={(e) => { setStyleFilter(e.target.value); setCurrentPage(1); }}
                  className="h-10 w-full appearance-none rounded-xl border border-background-300/60 bg-white px-3 pr-8 text-sm text-secondary-500 outline-none cursor-pointer"
                >
                  <option value="All">{t('tours.styleMobileAll')}</option>
                  <option value="multi-day">{t('tours.styleMultiDay')}</option>
                  <option value="day">{t('tours.styleDayTour')}</option>
                  <option value="cruise">{t('tours.styleCruise')}</option>
                </select>
                <SelectChevron className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400" />
              </div>
              <div className="relative">
                <select
                  value={durationFilter}
                  onChange={(e) => { setDurationFilter(e.target.value); setCurrentPage(1); }}
                  className="h-10 w-full appearance-none rounded-xl border border-background-300/60 bg-white px-3 pr-8 text-sm text-secondary-500 outline-none cursor-pointer"
                >
                  <option value="All">{t('tours.durationAll')}</option>
                  <option value="1-3 Days">{t('tours.days1to3')}</option>
                  <option value="4-7 Days">{t('tours.days4to7')}</option>
                  <option value="8+ Days">{t('tours.days8plus')}</option>
                </select>
                <SelectChevron className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="text-center py-8">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-background-200/70 overflow-hidden animate-pulse">
                <div className="aspect-[300/220] bg-background-200" />
                <div className="p-3.5 space-y-3">
                  <div className="flex gap-1.5">
                    <div className="h-5 w-12 bg-background-200 rounded-md" />
                    <div className="h-5 w-12 bg-background-200 rounded-md" />
                  </div>
                  <div className="h-4 bg-background-200 rounded w-3/4" />
                  <div className="h-8 bg-background-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : paginatedTours.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-map-pin-line text-4xl text-secondary-300 mb-3" />
            <p className="text-secondary-500 text-sm">{t('tours.noMatch')}</p>
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {paginatedTours.map((tour) => {
                const priceInfo = applyTourDiscount(
                  tour.price,
                  resolveTourDiscount(tour.id, discounts),
                );
                return (
                  <Link
                    key={tour.id}
                    to={`/tour/${tour.id}`}
                    className="group block h-full cursor-pointer"
                    aria-label={tour.title}
                  >
                    <article className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-white shadow-[0_4px_14px_-6px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-primary-400 hover:shadow-[0_18px_36px_-18px_rgba(16,185,129,0.35)]">
                      <div className="relative w-full overflow-hidden aspect-[300/220]">
                        <img
                          src={tour.image}
                          alt={tour.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                        <button
                          type="button"
                          aria-label={t('tours.addWishlist')}
                          aria-pressed={isFavorite(tour.id)}
                          onClick={(e) => { e.preventDefault(); toggleFavorite(tour.id); }}
                          className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-foreground-950 shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition hover:bg-white"
                        >
                          <i className={isFavorite(tour.id) ? 'ri-heart-fill text-red-500 text-sm' : 'ri-heart-line text-sm'} />
                        </button>
                      </div>
                      <div className="flex flex-1 flex-col gap-2.5 p-3.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-white px-2 py-1 text-[11px] font-medium text-foreground-950">
                            {tour.duration}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-white px-2 py-1 text-[11px] font-medium text-foreground-950">
                            {t('tours.spots', { count: tour.spots })}
                          </span>
                          {(tour.category || '').split(',').map((c) => c.trim()).filter(Boolean).map((cat) => (
                            <span key={cat} className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-white px-2 py-1 text-[11px] font-medium text-foreground-950">
                              {cat}
                            </span>
                          ))}
                          <span className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-white px-2 py-1 text-[11px] font-medium text-foreground-950">
                            {tour.group_type}
                          </span>
                        </div>
                        <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-foreground-950">
                          {tour.title}
                        </h3>
                        <div className="mt-auto flex items-end justify-between gap-2 border-t border-foreground-950/5 pt-3">
                          <div className="flex flex-col">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-secondary-400">
                              {t('tours.from')}
                            </span>
                            <span className="flex items-baseline gap-1.5">
                              {priceInfo.hasDiscount && (
                                <span className="text-[12px] font-medium text-secondary-400 line-through">
                                  {priceInfo.originalPrice.toLocaleString()}
                                </span>
                              )}
                              <span className="text-[15px] font-bold leading-tight text-primary-600">
                                {priceInfo.finalPrice.toLocaleString()} USD
                              </span>
                            </span>
                          </div>
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary-500 px-3 py-1.5 text-[12px] font-semibold text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-white whitespace-nowrap">
                            {t('tours.view')}
                            <i className="ri-arrow-right-line text-xs" />
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav aria-label={t('tours.pagination')} className="flex items-center justify-center gap-2 mt-8 lg:mt-10">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label={t('tours.prevPage')}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-secondary-200 bg-white text-foreground-950 transition-colors hover:border-primary-500 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Page ${i + 1}`}
                    aria-current={currentPage === i + 1 ? 'page' : undefined}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`flex h-10 w-10 items-center justify-center rounded-lg text-[12px] font-medium transition-colors ${
                      currentPage === i + 1
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'border border-secondary-200 bg-white text-foreground-950 hover:border-primary-500 hover:text-primary-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label={t('tours.nextPage')}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-secondary-200 bg-white text-foreground-950 transition-colors hover:border-primary-500 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </section>
  );
}